import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../../services/auth';

/**
 * ForgotPassword component for requesting password reset
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /**
   * Handle form submission
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await authService.requestPasswordReset(email);
      setMessage('Password reset instructions have been sent to your email.');
      setEmail(''); // Clear the form
    } catch (err) {
      setError(err.message || 'Failed to request password reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-700">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-white">
            Forgot your password?
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>
        
        {error && (
          <div className="rounded-md bg-red-900/30 p-4 border border-red-800/50" role="alert" aria-live="assertive">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-300">Error</h3>
                <div className="mt-2 text-sm text-red-400">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className="rounded-md bg-green-900/30 p-4 border border-green-800/50" role="status" aria-live="polite">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-300">Success</h3>
                <div className="mt-2 text-sm text-green-400">
                  <p>{message}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm py-1.5 px-3 focus:border-blue-500 focus:ring-blue-500 sm:text-sm placeholder-gray-400"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:bg-blue-300"
            >
              {loading ? 'Sending…' : 'Send reset instructions'}
            </button>
          </div>
          
          <div className="text-center">
            <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
